import { el, clear } from "./dom";
import { beginDrag, cellAt, diffCells, dragTo, gridKey, type Drag, type GridEdges, type GridModel } from "../shared/paint";
import { columnsPerPage, formatDayHead, formatMinutes, heatStep, LEVEL_LABEL, pagerLabel } from "../shared/present";
import { slotStarts, zonedParts } from "../shared/slots";
import type { EventDef, Level, Participant } from "../shared/types";
import type { Sync } from "../shared/sync";

export function buildGridModel(ev: EventDef, viewTz: string): GridModel {
  const slots = slotStarts(ev);
  const dates: string[] = [];
  const minutes: number[] = [];
  const map = new Map<string, number>();
  for (const s of slots) {
    const p = zonedParts(s, viewTz);
    if (!dates.includes(p.date)) dates.push(p.date);
    if (!minutes.includes(p.minutes)) minutes.push(p.minutes);
    map.set(`${p.date}|${p.minutes}`, s);
  }
  dates.sort();
  minutes.sort((a, b) => a - b);
  return {
    dates,
    minutes,
    slotAt(r, c) {
      const date = dates[c];
      const min = minutes[r];
      if (date === undefined || min === undefined) return null;
      return map.get(`${date}|${min}`) ?? null;
    },
  };
}

export function pageDates(model: GridModel, page: number, perPage: number): string[] {
  const start = page * perPage;
  return model.dates.slice(start, start + perPage);
}

export function perPageFor(elWidth: number, timeCol: number, cellMin: number, dateCount: number): number {
  return columnsPerPage(elWidth, timeCol, cellMin, dateCount);
}

function visibleModel(model: GridModel, dates: string[]): GridModel {
  const idx = dates.map((d) => model.dates.indexOf(d));
  return {
    dates,
    minutes: model.minutes,
    slotAt(r, c) {
      const full = idx[c];
      if (full === undefined || full < 0) return null;
      return model.slotAt(r, full);
    },
  };
}

export function mountPager(
  nav: HTMLElement,
  onPage: (page: number) => void,
): { update(dates: string[], page: number, perPage: number): void } {
  const prev = el("button", { type: "button", text: "Previous", "data-testid": "pager-prev" });
  const next = el("button", { type: "button", text: "Next", "data-testid": "pager-next" });
  const label = el("span", { "data-testid": "pager-label" });
  nav.append(prev, label, next);
  let page = 0;
  let pages = 1;
  prev.addEventListener("click", () => {
    if (page > 0) onPage(page - 1);
  });
  next.addEventListener("click", () => {
    if (page + 1 < pages) onPage(page + 1);
  });
  return {
    update(dates, nextPage, perPage) {
      page = nextPage;
      pages = Math.max(1, Math.ceil(dates.length / perPage));
      const slice = dates.slice(page * perPage, page * perPage + perPage);
      const show = dates.length > perPage;
      nav.hidden = !show;
      prev.disabled = page <= 0;
      next.disabled = page + 1 >= pages;
      label.textContent = pagerLabel(slice, page + 1, pages);
    },
  };
}

export function mountPaintGrid(
  table: HTMLTableElement,
  opts: {
    getSync: () => Sync;
    getBrush: () => Level;
    onPaint: (availability: Record<string, Level>) => void;
    /** Height, in px, of any fixed overlay (the brush bar under 700px) covering the bottom of the
     * viewport, so auto-scroll near "the bottom" does not trigger under it. Omit or return 0 when
     * nothing covers the bottom. */
    getBottomInset?: () => number;
  },
): {
  update(model: GridModel, dates: string[], availability: Record<string, Level>): void;
  isDragging(): boolean;
  refreshEdges(): void;
  destroy(): void;
} {
  let cells: HTMLTableCellElement[][] = [];
  let bySlot = new Map<string, HTMLTableCellElement>();
  let vis: GridModel | null = null;
  let lastKey = "";
  let lastAvail: Record<string, Level> = {};
  let drag: Drag | null = null;
  let live: Record<string, Level> = {};
  let focusR = 0;
  let focusC = 0;
  let scrollRaf = 0;
  let pointerY = 0;
  let pointerX = 0;
  let pointerId: number | null = null;
  let edges: GridEdges = { colLeft: [], colRight: [], rowTop: [], rowBottom: [] };

  function stopScroll(): void {
    if (scrollRaf) cancelAnimationFrame(scrollRaf);
    scrollRaf = 0;
  }

  function readEdges(): void {
    const heads = [...table.querySelectorAll("thead th")].filter((th) => !th.classList.contains("time"));
    const times = [...table.querySelectorAll("tbody th.time")];
    edges = {
      colLeft: heads.map((h) => h.getBoundingClientRect().left),
      colRight: heads.map((h) => h.getBoundingClientRect().right),
      rowTop: times.map((h) => h.getBoundingClientRect().top),
      rowBottom: times.map((h) => h.getBoundingClientRect().bottom),
    };
  }

  function paintAt(x: number, y: number): void {
    if (!drag || !vis) return;
    const loc = cellAt(edges, x, y);
    if (!loc) return;
    live = dragTo(drag, vis, loc.r, loc.c);
    applyAvail(live);
  }

  function autoScroll(): void {
    if (!drag || !vis) {
      stopScroll();
      return;
    }
    const band = 48;
    const bottom = window.innerHeight - (opts.getBottomInset?.() ?? 0);
    let dy = 0;
    if (pointerY < band) dy = -12;
    else if (pointerY > bottom - band) dy = 12;
    if (dy === 0) {
      stopScroll();
      return;
    }
    window.scrollBy(0, dy);
    readEdges();
    paintAt(pointerX, pointerY);
    scrollRaf = requestAnimationFrame(autoScroll);
  }

  function maybeScrollLoop(): void {
    const band = 48;
    const bottom = window.innerHeight - (opts.getBottomInset?.() ?? 0);
    const inside = pointerY < band || pointerY > bottom - band;
    if (inside && drag && !scrollRaf) scrollRaf = requestAnimationFrame(autoScroll);
    if (!inside) stopScroll();
  }

  function applyAvail(next: Record<string, Level>): void {
    if (!vis) return;
    const diffs = diffCells(lastAvail, next);
    for (const d of diffs) {
      const td = bySlot.get(d.slot);
      if (!td) continue;
      const r = Number(td.dataset.r);
      const c = Number(td.dataset.c);
      patchLevel(td, d.level, vis.dates[c] ?? "", vis.minutes[r] ?? 0);
    }
    lastAvail = { ...next };
  }

  function cellFromPoint(target: EventTarget | null): { r: number; c: number } | null {
    if (!(target instanceof HTMLElement)) return null;
    const td = target.closest("td.cell");
    if (!(td instanceof HTMLTableCellElement)) return null;
    const r = Number(td.dataset.r);
    const c = Number(td.dataset.c);
    if (!Number.isInteger(r) || !Number.isInteger(c)) return null;
    return { r, c };
  }

  function locked(): boolean {
    return opts.getSync().status === "locked" || opts.getSync().server?.locked != null;
  }

  /** Moves the roving-tabindex marker to (r, c) without stealing programmatic focus. Pointer
   * interactions call this (not setFocus) so a mouse or touch drag never yanks focus away, but the
   * DOM's tabIndex bookkeeping still tracks reality: without this, onFocusIn's later cleanup reads
   * the stale (never-marked) pointer position and clears the wrong cell, leaving two cells with
   * tabIndex 0. */
  function markFocus(r: number, c: number): void {
    const prev = cells[focusR]?.[focusC];
    if (prev) prev.tabIndex = -1;
    focusR = r;
    focusC = c;
    const next = cells[r]?.[c];
    if (next) next.tabIndex = 0;
  }

  function setFocus(r: number, c: number): void {
    markFocus(r, c);
    const next = cells[r]?.[c];
    if (next) next.focus();
  }

  function cancelDrag(): void {
    if (!drag) return;
    applyAvail(drag.base);
    drag = null;
    pointerId = null;
    stopScroll();
  }

  function endDrag(e: PointerEvent): void {
    if (!drag || pointerId !== e.pointerId) return;
    try {
      table.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    stopScroll();
    opts.onPaint(live);
    drag = null;
    pointerId = null;
  }

  function onPointerDown(e: PointerEvent): void {
    if (locked() || !vis) return;
    if (!e.isPrimary || drag) return;
    const loc = cellFromPoint(e.target);
    if (!loc) return;
    e.preventDefault();
    markFocus(loc.r, loc.c);
    pointerId = e.pointerId;
    table.setPointerCapture(e.pointerId);
    const current = { ...(opts.getSync().draft?.availability ?? lastAvail) };
    drag = beginDrag(current, vis, loc.r, loc.c, opts.getBrush());
    live = dragTo(drag, vis, loc.r, loc.c);
    applyAvail(live);
    pointerX = e.clientX;
    pointerY = e.clientY;
    readEdges();
    stopScroll();
    maybeScrollLoop();
  }

  function onPointerMove(e: PointerEvent): void {
    if (!drag || !vis || e.pointerId !== pointerId) return;
    pointerX = e.clientX;
    pointerY = e.clientY;
    paintAt(e.clientX, e.clientY);
    maybeScrollLoop();
  }

  function onKey(e: KeyboardEvent): void {
    if (locked() || !vis) return;
    const fromTarget = cellFromPoint(e.target);
    const r = fromTarget?.r ?? focusR;
    const c = fromTarget?.c ?? focusC;
    const rows = vis.minutes.length;
    const cols = vis.dates.length;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocus(Math.min(rows - 1, r + 1), c);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocus(Math.max(0, r - 1), c);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      setFocus(r, Math.min(cols - 1, c + 1));
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      setFocus(r, Math.max(0, c - 1));
    } else if (e.key === " " || e.key === "Enter") {
      e.preventDefault();
      const current = { ...(opts.getSync().draft?.availability ?? lastAvail) };
      const d = beginDrag(current, vis, r, c, opts.getBrush());
      opts.onPaint(dragTo(d, vis, r, c));
    }
  }

  function onFocusIn(e: FocusEvent): void {
    const loc = cellFromPoint(e.target);
    if (!loc) return;
    const prev = cells[focusR]?.[focusC];
    if (prev && prev !== cells[loc.r]?.[loc.c]) prev.tabIndex = -1;
    focusR = loc.r;
    focusC = loc.c;
    const td = cells[loc.r]?.[loc.c];
    if (td) td.tabIndex = 0;
  }

  function onScroll(): void {
    if (drag) readEdges();
  }

  table.addEventListener("pointerdown", onPointerDown);
  table.addEventListener("pointermove", onPointerMove);
  table.addEventListener("pointerup", endDrag);
  table.addEventListener("pointercancel", endDrag);
  table.addEventListener("lostpointercapture", endDrag);
  table.addEventListener("keydown", onKey);
  table.addEventListener("focusin", onFocusIn);
  window.addEventListener("scroll", onScroll, { passive: true });

  return {
    update(model, dates, availability) {
      const visNext = visibleModel(model, dates);
      const key = gridKey(visNext);
      vis = visNext;
      if (key !== lastKey) {
        if (drag) cancelDrag();
        lastKey = key;
        const built = rebuild(table, visNext, availability);
        cells = built.cells;
        bySlot = built.bySlot;
        lastAvail = { ...availability };
        const keep = cells[focusR]?.[focusC];
        if (keep) keep.tabIndex = 0;
        else if (cells[0]?.[0]) {
          focusR = 0;
          focusC = 0;
          cells[0][0].tabIndex = 0;
        }
        return;
      }
      applyAvail(availability);
    },
    isDragging() {
      return drag !== null;
    },
    refreshEdges() {
      if (drag) readEdges();
    },
    destroy() {
      stopScroll();
      table.removeEventListener("pointerdown", onPointerDown);
      table.removeEventListener("pointermove", onPointerMove);
      table.removeEventListener("pointerup", endDrag);
      table.removeEventListener("pointercancel", endDrag);
      table.removeEventListener("lostpointercapture", endDrag);
      table.removeEventListener("keydown", onKey);
      table.removeEventListener("focusin", onFocusIn);
      window.removeEventListener("scroll", onScroll);
    },
  };
}

function rebuild(
  table: HTMLTableElement,
  model: GridModel,
  availability: Record<string, Level>,
): { cells: HTMLTableCellElement[][]; bySlot: Map<string, HTMLTableCellElement> } {
  clear(table);
  const thead = el("thead");
  const hr = el("tr");
  hr.append(el("th", { class: "time", text: "" }));
  for (const d of model.dates) hr.append(el("th", { text: formatDayHead(d) }));
  thead.append(hr);
  const tbody = el("tbody");
  const matrix: HTMLTableCellElement[][] = [];
  const bySlot = new Map<string, HTMLTableCellElement>();
  for (let r = 0; r < model.minutes.length; r++) {
    const min = model.minutes[r] ?? 0;
    const tr = el("tr");
    tr.append(el("th", { class: "time", text: formatMinutes(min) }));
    const row: HTMLTableCellElement[] = [];
    for (let c = 0; c < model.dates.length; c++) {
      const slot = model.slotAt(r, c);
      const td = el("td", {
        class: "cell",
        "data-r": String(r),
        "data-c": String(c),
      });
      if (slot !== null) {
        td.dataset.slot = String(slot);
        bySlot.set(String(slot), td);
      }
      const level = slot === null ? 0 : (availability[String(slot)] ?? 0);
      patchLevel(td, level, model.dates[c] ?? "", min);
      td.tabIndex = -1;
      tr.append(td);
      row.push(td);
    }
    matrix.push(row);
    tbody.append(tr);
  }
  table.append(thead, tbody);
  return { cells: matrix, bySlot };
}

function patchLevel(td: HTMLTableCellElement, level: Level, date: string, minutes: number): void {
  if (level === 0) td.removeAttribute("data-level");
  else td.dataset.level = String(level);
  td.setAttribute("aria-label", `${formatDayHead(date)} ${formatMinutes(minutes)}, ${LEVEL_LABEL[level]}`);
}

export function mountHeatGrid(
  table: HTMLTableElement,
  opts: {
    onInspect: (slot: number, cell: HTMLTableCellElement) => void;
  },
): {
  update(
    model: GridModel,
    dates: string[],
    participants: Participant[],
    filterPid: string | null,
  ): void;
} {
  let lastKey = "";
  let cells: HTMLTableCellElement[][] = [];
  let vis: GridModel | null = null;

  table.addEventListener("click", (e) => {
    const td = e.target instanceof Element ? e.target.closest("td.heat") : null;
    if (!(td instanceof HTMLTableCellElement) || !td.dataset.slot) return;
    opts.onInspect(Number(td.dataset.slot), td);
  });
  table.addEventListener("pointerover", (e) => {
    const td = e.target instanceof Element ? e.target.closest("td.heat") : null;
    if (!(td instanceof HTMLTableCellElement) || !td.dataset.slot) return;
    if (e.pointerType === "mouse") opts.onInspect(Number(td.dataset.slot), td);
  });

  return {
    update(model, dates, participants, filterPid) {
      const visNext = visibleModel(model, dates);
      const key = gridKey(visNext);
      vis = visNext;
      const people = filterPid ? participants.filter((p) => p.id === filterPid) : participants;
      if (key !== lastKey) {
        lastKey = key;
        cells = rebuildHeat(table, visNext, people);
        return;
      }
      if (!vis) return;
      for (let r = 0; r < vis.minutes.length; r++) {
        for (let c = 0; c < vis.dates.length; c++) {
          const td = cells[r]?.[c];
          const slot = vis.slotAt(r, c);
          if (!td || slot === null) continue;
          paintHeatCell(td, slot, people);
        }
      }
    },
  };
}

function rebuildHeat(table: HTMLTableElement, model: GridModel, people: Participant[]): HTMLTableCellElement[][] {
  clear(table);
  const thead = el("thead");
  const hr = el("tr");
  hr.append(el("th", { class: "time", text: "" }));
  for (const d of model.dates) hr.append(el("th", { text: formatDayHead(d) }));
  thead.append(hr);
  const tbody = el("tbody");
  const matrix: HTMLTableCellElement[][] = [];
  for (let r = 0; r < model.minutes.length; r++) {
    const min = model.minutes[r] ?? 0;
    const tr = el("tr");
    tr.append(el("th", { class: "time", text: formatMinutes(min) }));
    const row: HTMLTableCellElement[] = [];
    for (let c = 0; c < model.dates.length; c++) {
      const slot = model.slotAt(r, c);
      const td = el("td", { class: "heat", "data-r": String(r), "data-c": String(c) });
      if (slot !== null) {
        td.dataset.slot = String(slot);
        paintHeatCell(td, slot, people);
      }
      tr.append(td);
      row.push(td);
    }
    matrix.push(row);
    tbody.append(tr);
  }
  table.append(thead, tbody);
  return matrix;
}

function paintHeatCell(td: HTMLTableCellElement, slot: number, people: Participant[]): void {
  const total = people.length;
  let available = 0;
  for (const p of people) {
    const lv = p.availability[String(slot)] ?? 0;
    if (lv >= 1) available += 1;
  }
  const step = heatStep(available, total);
  td.dataset.step = String(step);
  if (total > 0 && available === total) td.dataset.all = "1";
  else td.removeAttribute("data-all");
  td.textContent = available > 0 ? String(available) : "";
}
